'use client';

import { getClientAuthHeaders } from '@/lib/auth/client-api';
import { getFirebaseAuthClient } from '@/lib/firebase/client';
import { assertReaderIdentity, readerClientIdentity } from './client-identity';

export class ReaderClientError extends Error {
  constructor(message: string, public readonly status: number, public readonly data: unknown) {
    super(message);
    this.name = 'ReaderClientError';
  }
}

function checkAbort(signal?: AbortSignal | null) {
  if (signal?.aborted) throw signal.reason ?? new DOMException('Reader request cancelled.', 'AbortError');
}
function waitForReaderRetry(milliseconds: number, signal?: AbortSignal | null) {
  checkAbort(signal);
  return new Promise<void>((resolve, reject) => {
    const abort = () => { clearTimeout(timer); reject(signal?.reason ?? new DOMException('Reader request cancelled.', 'AbortError')); };
    const timer = setTimeout(() => { signal?.removeEventListener('abort', abort); resolve(); }, milliseconds);
    signal?.addEventListener('abort', abort, { once: true });
  });
}

export async function readerRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  if (!path.startsWith('/api/reader/')) throw new Error('Reader requests must use the private reader API.');
  const identity = readerClientIdentity();
  assertReaderIdentity(identity);
  checkAbort(options.signal);
  const headers = await getClientAuthHeaders(options.headers);
  assertReaderIdentity(identity);
  const currentUserUid = getFirebaseAuthClient()?.currentUser?.uid;
  if (currentUserUid && currentUserUid !== identity.uid) throw new Error('The signed-in account changed. Open the reader again to continue.');
  headers.set('X-Reader-User-Uid', identity.uid!);
  if (options.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  // Seven total GET attempts and 15 seconds of bounded waiting allow admitted
  // work to drain. Mutations and other failures remain explicit user retries.
  const retryDelays = [250, 750, 1_500, 2_500, 4_000, 6_000];
  for (let attempt = 0; ; attempt++) {
    assertReaderIdentity(identity); checkAbort(options.signal);
    const response = await fetch(path, { ...options, headers, credentials: 'same-origin', cache: 'no-store' });
    const payload = await response.json().catch(() => null) as { ok?: boolean; data?: T; error?: string | { code?: string; message?: string }; message?: string } | null;
    assertReaderIdentity(identity); checkAbort(options.signal);
    const isBusy = response.status === 503 && typeof payload?.error === 'object' && payload.error.code === 'READER_BUSY';
    if ((options.method || 'GET').toUpperCase() === 'GET' && isBusy && attempt < retryDelays.length) {
      await waitForReaderRetry(retryDelays[attempt], options.signal);
      assertReaderIdentity(identity); checkAbort(options.signal);
      continue;
    }
    if (!response.ok || !payload?.ok) {
      const message = typeof payload?.error === 'string' ? payload.error : payload?.error?.message || payload?.message;
      throw new ReaderClientError(message || 'The reader request could not be completed. Please try again.', response.status, payload);
    }
    return payload.data as T;
  }
}
